import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useAlert } from 'contexts/alert/useAlert';
import { TimeoutError } from 'data/errors/TimeoutError';
import { ArtifactType, usePublishArtifactSettingMutation } from 'data/generated/graphql';
import { serializeLayoutForPublish } from 'utils/layoutSerialize';
import { EditorLayout } from 'utils/layoutUtils';

interface UseLayoutPublishProps {
  publicationKey: string;
}

interface UseLayoutPublishReturn {
  publishLayout: (layout: EditorLayout, artifactId: string) => Promise<void>;
  isPublishing: boolean;
}

export const useLayoutPublish = ({ publicationKey }: UseLayoutPublishProps): UseLayoutPublishReturn => {
  const { alertSuccess, alertError, alertWarning } = useAlert();
  const queryClient = useQueryClient();
  const { mutateAsync: publishArtifactSettingMutation, isLoading } = usePublishArtifactSettingMutation();
  const isPublishing: boolean = isLoading;

  const publishLayout: (layout: EditorLayout, artifactId: string) => Promise<void> = useCallback(
    async (layout: EditorLayout, artifactId: string) => {
      try {
        const body = serializeLayoutForPublish(layout);

        await publishArtifactSettingMutation({
          publicationKey,
          artifactId,
          artifactType: ArtifactType.Layout,
          body
        });

        await queryClient.invalidateQueries(['GetArtifactSettingHistory']);
        alertSuccess('Published layout.');
      } catch (error) {
        if (error instanceof TimeoutError) {
          alertWarning(
            'The page publish request is taking longer than expected. The page may have been published successfully. Please check the publish history or refresh manually to verify.'
          );
          return;
        }

        if (error instanceof Error) {
          alertError(`Could not publish the page. ${error.message}`, { wrapNewlines: true });
        } else {
          alertError('Could not publish the page');
        }

        throw error;
      }
    },
    [publishArtifactSettingMutation, publicationKey, queryClient, alertSuccess, alertWarning, alertError]
  );

  return {
    publishLayout,
    isPublishing
  };
};
